import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, MapPin, Smartphone, Gift, X, ShoppingBag, Star, BadgeCheck, Mail, Users, Award } from 'lucide-react';
import { OptimizedImage } from '../components/OptimizedImage';
import { FeaturedProductCard } from '../components/ProductCard';

const HERO_IMAGE = "https://images.pexels.com/photos/31337078/pexels-photo-31337078.jpeg";

/* ─── Hero ─── */
export const HeroSection = () => (
  <section className="min-h-[90vh] grid grid-cols-1 lg:grid-cols-2" data-testid="hero-section">
    <div className="flex items-center justify-center px-6 md:px-12 lg:px-24 py-20 lg:py-0 order-2 lg:order-1 bg-[#F7F7EB] min-h-[40vh] lg:min-h-[90vh]">
      <div className="max-w-xl">
        <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-6 animate-fade-in-up">Dancewear & Activewear</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none text-[#000000] mb-8 animate-fade-in-up animation-delay-100">
          Move. <br />
          <span className="italic">Express.</span> <br />
          Empower.
        </h1>
        <p className="text-lg text-[#737373] leading-relaxed mb-10 animate-fade-in-up animation-delay-200">
          Performance wear that moves with you, supports you and empowers you. 
          Dance. Fitness. You.
        </p>
        <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
          <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="hero-shop-btn">
            Shop Collection <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/shop?sort=newest" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-new-arrivals-btn">
            New Arrivals
          </Link>
        </div>
      </div>
    </div>
    <div className="relative h-[50vh] lg:h-auto order-1 lg:order-2">
      <OptimizedImage
        src={HERO_IMAGE}
        alt="Graceful ballet dancer"
        className="w-full h-full object-cover"
        width={1200}
        loading="eager"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent lg:bg-gradient-to-l lg:from-black/10 lg:to-transparent" />
    </div>
  </section>
);

/* ─── Shop by Category ─── */
const CATEGORY_DEFAULTS = [
  { key: 'dance', title: 'Dance', subtitle: 'Leotards, shoes & tights', href: '/collections/dance' },
  { key: 'activewear', title: 'Activewear', subtitle: 'Tops, leggings & shorts', href: '/collections/activewear' },
  { key: 'accessories', title: 'Accessories', subtitle: 'Bags, hair & essentials', href: '/collections/accessories' },
  { key: 'new-arrivals', title: 'New Arrivals', subtitle: 'Just landed', href: '/shop?sort=newest' },
];

export const ShopByCategory = ({ categoryImages }) => (
  <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white" data-testid="shop-by-category">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-10 md:mb-14">
        <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-2">Start Shopping</p>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#000]">Shop by Category</h2>
      </div>
      <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-4 md:pb-0 -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide">
        {CATEGORY_DEFAULTS.map((cat) => (
          <Link
            key={cat.key}
            to={cat.href}
            className="group relative flex-shrink-0 w-[72vw] sm:w-[48vw] md:w-auto snap-start overflow-hidden"
            data-testid={`category-card-${cat.key}`}
          >
            <div className="relative aspect-[3/4]">
              <img
                src={categoryImages[cat.key] || ''}
                alt={cat.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <p className="text-[11px] uppercase tracking-[0.15em] text-white/60 mb-1">{cat.subtitle}</p>
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-3 leading-tight">{cat.title}</h3>
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-medium text-white border-b border-white/40 pb-0.5 group-hover:border-[#DEA193] group-hover:text-[#DEA193] transition-colors">
                  Shop {cat.title} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Referral Welcome Banner ─── */
export const ReferralBanner = ({ referralCode, show, onDismiss }) => {
  if (!show || !referralCode) return null;
  return (
    <div className="relative bg-[#DEA193] overflow-hidden" data-testid="referral-welcome-banner">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#000000]" />
      <button
        onClick={onDismiss}
        className="absolute top-4 right-4 z-10 p-1.5 bg-black/10 hover:bg-black/20 rounded-sm transition-colors"
        data-testid="referral-banner-close"
      >
        <X className="w-4 h-4 text-[#000000]" />
      </button>
      <div className="flex flex-col items-center justify-center text-center px-6 py-10 md:py-14 max-w-2xl mx-auto">
        <Gift className="w-8 h-8 text-[#000000] mb-4 animate-fade-in-up" />
        <p className="text-xs uppercase tracking-[0.25em] text-[#000000]/60 mb-2 animate-fade-in-up">Welcome Offer</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#000000] mb-3 animate-fade-in-up animation-delay-100">10% Off Your First Order</h2>
        <p className="text-sm text-[#000000]/70 mb-6 animate-fade-in-up animation-delay-200">
          You&apos;ve been referred! Create an account to claim your exclusive 10% discount on orders over &pound;50.
        </p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 bg-[#000000] text-white px-8 py-3 text-xs uppercase tracking-widest font-medium hover:bg-[#222222] transition-colors animate-fade-in-up animation-delay-300"
          data-testid="referral-banner-cta"
        >
          Create Account &amp; Claim <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#000000]" />
    </div>
  );
};

/* ─── Promotional Banners ─── */
export const PromoBanners = ({ banners }) => {
  if (!banners.length) return null;
  return (
    <section data-testid="promo-banners">
      {banners.map((banner) => {
        const align = banner.layout === 'left' ? 'text-left items-start' : banner.layout === 'right' ? 'text-right items-end' : 'text-center items-center';
        const hasImage = !!banner.bg_image_url;
        return (
          <div key={banner.id} className="relative overflow-hidden" style={{ backgroundColor: banner.bg_color }} data-testid={`promo-banner-${banner.id}`}>
            {hasImage && (
              <>
                <OptimizedImage src={banner.bg_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" width={1400} showSkeleton={false} />
                <div className="absolute inset-0 bg-black/40" />
              </>
            )}
            <div className="absolute top-0 left-0 right-0 h-[3px] z-10" style={{ backgroundColor: banner.accent_color }} />
            <div className={`relative z-10 flex flex-col justify-center ${align} px-6 md:px-12 lg:px-24 py-14 md:py-20 max-w-5xl ${banner.layout === 'centered' ? 'mx-auto' : banner.layout === 'right' ? 'ml-auto' : ''}`}>
              {banner.subtitle && (
                <p className="text-xs uppercase tracking-[0.25em] mb-4 animate-fade-in-up" style={{ color: banner.accent_color }}>
                  {banner.subtitle}
                </p>
              )}
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight animate-fade-in-up animation-delay-100" style={{ color: banner.text_color }}>
                {banner.title}
              </h2>
              {banner.cta_text && banner.cta_link && (
                <Link
                  to={banner.cta_link}
                  className="inline-flex items-center gap-2 px-8 py-3 text-xs uppercase tracking-widest font-medium transition-opacity hover:opacity-80 animate-fade-in-up animation-delay-200"
                  style={{ backgroundColor: banner.accent_color, color: banner.bg_color }}
                  data-testid={`promo-banner-cta-${banner.id}`}
                >
                  {banner.cta_text} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-[3px] z-10" style={{ backgroundColor: banner.accent_color }} />
          </div>
        );
      })}
    </section>
  );
};

/* ─── Featured Brands ─── */
export const FeaturedBrands = ({ brands }) => {
  if (!brands.length) return null;
  return (
    <section className="py-20 px-6 md:px-12 lg:px-24 bg-white" data-testid="featured-brands-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-2">Our Partners</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#000]">Featured Brands</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-8">
          {brands.map((brand) => (
            <Link
              key={brand.id}
              to={`/brand/${encodeURIComponent(brand.id)}`}
              className="group relative overflow-hidden"
              data-testid={`featured-brand-${brand.id}`}
            >
              <div className="relative aspect-[4/5]">
                {brand.hero_image ? (
                  <img
                    src={brand.hero_image}
                    alt={brand.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[#1a1a1a] flex items-center justify-center">
                    <span className="font-serif text-3xl md:text-4xl text-white/90 tracking-tight text-center px-4 leading-tight">
                      {brand.name}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                {brand.logo_url && (
                  <div className="absolute top-4 left-4 z-10">
                    <img src={brand.logo_url} alt="" className="h-6 md:h-8 w-auto object-contain drop-shadow-lg" loading="lazy" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-5">
                  {brand.hero_image && (
                    <h3 className="font-serif text-lg md:text-2xl text-white mb-0.5 leading-tight truncate">
                      {brand.name}
                    </h3>
                  )}
                  <p className="text-[10px] md:text-[11px] uppercase tracking-[0.12em] text-white/50 mb-2">
                    {brand.product_count} product{brand.product_count !== 1 ? 's' : ''}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[10px] md:text-xs uppercase tracking-[0.12em] font-medium text-white border-b border-white/30 pb-0.5 group-hover:border-[#DEA193] group-hover:text-[#DEA193] transition-colors whitespace-nowrap">
                    Shop Now <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center">
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[#000] hover:text-[#DEA193] transition-colors" data-testid="shop-all-brands-link">
            Shop All Brands <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ─── Featured Products ─── */
export const FeaturedProducts = ({ products, loading }) => (
  <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#F7F7EB]" data-testid="featured-section">
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-end mb-12">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-2">Curated Selection</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#000000]">Featured Products</h2>
        </div>
        <Link to="/shop?featured=true" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[#000000] hover:text-[#DEA193] transition-colors">
          View All <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/5] bg-[#E5D8D0] mb-6" />
              <div className="h-4 bg-[#E5D8D0] w-1/3 mb-2" />
              <div className="h-6 bg-[#E5D8D0] w-2/3 mb-2" />
              <div className="h-5 bg-[#E5D8D0] w-1/4" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {products.map((product, index) => (
            <div key={product.id} className={`animate-fade-in-up animation-delay-${(index + 1) * 100}`}>
              <FeaturedProductCard product={product} />
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);

/* ─── Social Proof ─── */
export const SocialProof = ({ reviews }) => {
  if (!reviews.length) return null;
  return (
    <section className="py-20 px-6 md:px-12 lg:px-24 bg-white" data-testid="social-proof-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-2">Real Reviews</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#000]">Loved by the Split Soul Community</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reviews.map((review) => (
            <div key={review.id} className="p-6 border border-[#E5D8D0] bg-[#FAFAF5]" data-testid={`homepage-review-${review.id}`}>
              <div className="flex gap-0.5 mb-3">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#DEA193] text-[#DEA193]' : 'fill-none text-[#E5D8D0]'}`} />
                ))}
              </div>
              {review.title && <p className="font-medium text-[#000] mb-2 line-clamp-1">{review.title}</p>}
              {review.comment && <p className="text-sm text-[#737373] leading-relaxed mb-4 line-clamp-3">{review.comment}</p>}
              <div className="flex items-center gap-2 mt-auto pt-3 border-t border-[#E5D8D0]">
                <div className="w-7 h-7 bg-[#DEA193] rounded-full flex items-center justify-center text-white text-xs font-medium">
                  {review.user_name?.charAt(0)?.toUpperCase() || 'C'}
                </div>
                <div>
                  <p className="text-xs font-medium text-[#000]">{review.user_name}</p>
                  {review.verified_purchase && (
                    <span className="flex items-center gap-0.5 text-[10px] text-green-700"><BadgeCheck className="w-3 h-3" /> Verified</span>
                  )}
                </div>
                {review.product_name && (
                  <span className="ml-auto text-[10px] text-[#999] truncate max-w-[100px]">{review.product_name}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Loyalty CTA ─── */
export const LoyaltyCTA = () => (
  <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#000]" data-testid="loyalty-section">
    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-3">Rewards Programme</p>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-5">Move More. Earn More.</h2>
        <p className="text-lg text-white/60 leading-relaxed mb-8">
          Join Split Soul Rewards and earn rewards every time you shop. Redeem points for discounts on future orders.
        </p>
        <Link
          to="/loyalty"
          className="inline-flex items-center gap-2 bg-[#DEA193] text-[#000] px-8 py-3 text-xs uppercase tracking-widest font-medium hover:bg-[#d09585] transition-colors"
          data-testid="loyalty-cta-btn"
        >
          Join Split Soul Rewards <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[
          { icon: ShoppingBag, title: 'Shop', desc: 'Earn on every order' },
          { icon: Award, title: 'Earn', desc: 'Points build up fast' },
          { icon: Gift, title: 'Redeem', desc: 'Discounts & perks' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="text-center p-4 border border-white/10 rounded-sm">
            <Icon className="w-6 h-6 text-[#DEA193] mx-auto mb-2" />
            <p className="text-white text-sm font-medium mb-1">{title}</p>
            <p className="text-[11px] text-white/50">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Refer a Friend ─── */
export const ReferralCTA = () => (
  <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F7F7EB] border-y border-[#E5D8D0]" data-testid="referral-section">
    <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12">
      <div className="flex-shrink-0 w-16 h-16 bg-[#DEA193] rounded-full flex items-center justify-center">
        <Users className="w-7 h-7 text-white" />
      </div>
      <div className="flex-1 text-center md:text-left">
        <h3 className="font-serif text-2xl sm:text-3xl text-[#000] mb-2">Share the Soul</h3>
        <p className="text-[#737373] text-sm leading-relaxed">
          Love Split Soul? Refer a friend and you both benefit. Check the referral page for current terms.
        </p>
      </div>
      <Link
        to="/referrals"
        className="flex-shrink-0 inline-flex items-center gap-2 border border-[#000] text-[#000] px-6 py-3 text-xs uppercase tracking-widest font-medium hover:bg-[#000] hover:text-white transition-colors"
        data-testid="referral-cta-btn"
      >
        Refer a Friend <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

/* ─── Events Showcase ─── */
export const EventsShowcase = ({ events }) => {
  if (!events.length) return null;
  const now = new Date();
  const upcoming = events.filter(e => new Date(e.date) >= now);
  const past = events.filter(e => new Date(e.date) < now);

  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 bg-white" data-testid="events-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-3">Shop In Person</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#000] mb-6">Find Us at Events</h2>
            <p className="text-lg text-[#737373] leading-relaxed mb-8">
              Try before you buy, discover new brands and shop Split Soul in person.
            </p>
            <div className="flex items-center gap-3 text-sm text-[#000]">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F7F7EB] border border-[#E5D8D0]">
                <Smartphone className="w-4 h-4 text-[#DEA193]" />
                <span>Scan & Buy via QR</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F7F7EB] border border-[#E5D8D0]">
                <MapPin className="w-4 h-4 text-[#DEA193]" />
                <span>In-Person Shopping</span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {upcoming.length > 0 && (
              <>
                <p className="text-xs uppercase tracking-[0.2em] text-[#DEA193] font-medium mb-2">Upcoming Events</p>
                {upcoming.map((event) => {
                  const d = new Date(event.date);
                  return (
                    <div key={event.id} className="flex gap-5 p-5 bg-[#F7F7EB] border border-[#E5D8D0] hover:border-[#DEA193] transition-colors group cursor-pointer" data-testid={`event-card-${event.id}`} onClick={() => event.slug && (window.location.href = `/events/${event.slug}`)}>
                      <div className="flex-shrink-0 w-16 h-16 bg-[#000] text-white flex flex-col items-center justify-center group-hover:bg-[#DEA193] transition-colors">
                        <span className="text-lg font-bold leading-none">{d.getDate()}</span>
                        <span className="text-[10px] tracking-widest mt-0.5">{d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#000] text-lg truncate">{event.name}</h3>
                        <div className="flex items-center gap-1.5 text-sm text-[#737373] mt-1">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{event.location}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {past.length > 0 && (
              <>
                <p className="text-xs uppercase tracking-[0.2em] text-[#999] font-medium mb-2 mt-6">Previous Events</p>
                {past.slice(0, 3).map((event) => {
                  const d = new Date(event.date);
                  return (
                    <div key={event.id} className="flex gap-4 p-4 border border-[#E5D8D0] opacity-70" data-testid={`past-event-${event.id}`}>
                      <div className="flex-shrink-0 w-12 h-12 bg-[#E5D8D0] text-[#737373] flex flex-col items-center justify-center">
                        <span className="text-sm font-bold leading-none">{d.getDate()}</span>
                        <span className="text-[9px] tracking-widest">{d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#000] truncate">{event.name}</h3>
                        <div className="flex items-center gap-1.5 text-xs text-[#999] mt-0.5">
                          <MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{event.location}</span>
                        </div>
                      </div>
                      {event.poster_url && (
                        <img src={event.poster_url} alt="" className="w-12 h-12 object-cover rounded-sm flex-shrink-0" loading="lazy" />
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Email Signup ─── */
export const EmailSignup = ({ emailInput, setEmailInput, emailSubmitting, onSubmit }) => (
  <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#000]" data-testid="email-signup-section">
    <div className="max-w-2xl mx-auto text-center">
      <Mail className="w-8 h-8 text-[#DEA193] mx-auto mb-4" />
      <p className="text-sm uppercase tracking-[0.2em] text-[#DEA193] mb-3">Stay Connected</p>
      <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-4">Be First to Know</h2>
      <p className="text-white/50 mb-8 leading-relaxed">
        New drops, event invites and member-only offers — straight from Split Soul.
      </p>
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="email-signup-form">
        <input
          type="email"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          placeholder="your@email.com"
          required
          className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder:text-white/30 text-sm focus:border-[#DEA193] focus:outline-none transition-colors"
          data-testid="email-signup-input"
        />
        <button
          type="submit"
          disabled={emailSubmitting}
          className="px-6 py-3 bg-[#DEA193] text-[#000] text-xs uppercase tracking-widest font-medium hover:bg-[#d09585] disabled:opacity-50 transition-colors whitespace-nowrap"
          data-testid="email-signup-submit"
        >
          {emailSubmitting ? 'Joining...' : 'Join the Community'}
        </button>
      </form>
      <p className="text-[11px] text-white/30 mt-4">No spam. Unsubscribe anytime.</p>
    </div>
  </section>
);
